import {Component, OnInit, ViewChild} from '@angular/core';
import {
    GiftCard,
    GiftCardService,
    LoadingService,
    Organization,
    OrganizationGiftCardSummary,
    OrganizationService,
    PickupLocation,
    PickupLocationService,
    PrinterService,
    SubscriberComponent,
    SystemReportService,
    ToastService
} from 'brewbill-lib';
import {ModalController, NavController, Platform} from '@ionic/angular';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {forkJoin} from 'rxjs';
import {GiftCardComponent} from './gift-card/gift-card.component';
import write_blob from 'capacitor-blob-writer';
import {Directory} from '@capacitor/filesystem';
import {FileOpener} from '@ionic-native/file-opener/ngx';

@Component({
    template: ''
})
export abstract class BaseGiftCardTableComponent extends SubscriberComponent implements OnInit {
    searchCriteria: any;
    organization: Organization;
    pickupLocation: PickupLocation;
    dataSource: MatTableDataSource<GiftCard>;
    filterString = '';
    organizationSummary: OrganizationGiftCardSummary;
    status = ['ACTIVE'];
    displayedColumns: string[] = ['cardNumber', 'status', 'createDate', 'createdBy', 'lastActive', 'initialAmount', 'balance', 'print'];
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    constructor(
        private giftCardService: GiftCardService,
        private organizationService: OrganizationService,
        private pickupLocationService: PickupLocationService,
        private systemReportService: SystemReportService,
        private printerService: PrinterService,
        private loadingService: LoadingService,
        private toastService: ToastService,
        private modalController: ModalController,
        private navController: NavController,
        private platform: Platform,
        private fileOpener: FileOpener
    ) {
        super();
    }

    ngOnInit() {
        this.pickupLocation = this.pickupLocationService.currentValue;
        this.organization = this.organizationService.currentValue;
        if (!this.pickupLocation && !!this.organization) {
            this.pickupLocation = this.organization.pickupLocations[0];
        }

        this.searchCriteria = this.giftCardService.getSearchCriteria();
        if (!!this.searchCriteria) {
            this.filterString = this.searchCriteria.filter;
        }

        this.dataSource = new MatTableDataSource([]);
        this.refresh();
    }

    refresh() {
        this.loadingService.present();

        let query = this.giftCardService.findActiveByOrganization(this.organization.id);
        if (this.status.length === 2 || this.status.length === 0) {
            query = this.giftCardService.findAllByOrganization(this.organization.id);
        } else if (this.status[0] === 'COMPLETE') {
            query = this.giftCardService.findCompletedByOrganization(this.organization.id);
        }

        forkJoin([
            query,
            this.giftCardService.findSummaryByOrganization(this.organization.id, (!!this.status && this.status.length === 1)
                ? this.status[0]
                : null)
        ]).subscribe(([giftCards, summary]) => {
            this.dataSource = new MatTableDataSource(giftCards as GiftCard[]);
            this.dataSource.sort = this.sort;
            this.organizationSummary = summary as OrganizationGiftCardSummary;
            this.applyFilter(this.filterString);
            this.loadingService.dismiss();
        });
    }

    applyFilter(filterValue: string) {
        this.giftCardService.setSearchCriteria({filter: filterValue});
        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    displayGiftCard(giftCard) {
        this.loadingService.present();
        this.giftCardService.get(giftCard.id).subscribe(async g => {
            const modal = await this.modalController.create({
                component: GiftCardComponent,
                componentProps: {
                    giftCard: new GiftCard(g),
                    organization: this.organization
                }
            });

            modal.onDidDismiss().then((dataReturned) => {
                if (!!dataReturned && !!dataReturned.data) {
                    this.loadGiftCard(new GiftCard(dataReturned.data));
                }
            });

            await modal.present();
            this.loadingService.dismiss();
        });
    }

    async loadGiftCard(giftCard = new GiftCard()) {
        this.giftCardService.setLoadingGiftCard(giftCard);
        await this.navController.navigateBack(`/manager/${this.pickupLocation.id}/manual-order`);
    }

    async print(event, giftCard: GiftCard) {
        event.stopPropagation();
        if (this.printerService.isConnected()) {
            this.printerService.printGiftCardBalances(this.organization, [giftCard]);
        } else {
            this.toastService.error('The receipt could not be printed.');
        }
    }

    async exportRawData(event?) {
        this.loadingService.present();

        this.systemReportService
            .getGiftCardLiabilityCsv(this.organization.id, (!!this.status && this.status.length === 1)
                ? this.status[0]
                : null)
            .subscribe(async (data: any) => {
                await this.saveFile(data, 'giftCardLiability.csv');
            }, async () => {
                await this.toastService.error('Error generating the payment export.');
            }, () => {
                if (!!event) {
                    event.target.complete();
                }
            });
    }

    async saveFile(dataBlob, filename) {
        if (this.platform.is('ios') || this.platform.is('android')) {
            write_blob({
                path: filename,
                directory: Directory.Data,
                blob: dataBlob,
            }).then(async uri => {
                await this.fileOpener.open(uri, 'text/csv').catch(e => {
                    console.log(e);
                    this.toastService.error('The file was saved but could not be opened.');
                });
            }).catch(err => {
                console.log(err);
                this.toastService.error('The file was not saved.');
            }).finally(() => this.loadingService.dismiss());
        } else {
            const downloadURL = window.URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = downloadURL;
            link.download = filename;
            link.click();
            this.loadingService.dismiss();
        }
    }
}
